/**
 * MarkdownNotes — renders markdown source via `marked` with the editorial
 * `prose-notes` styles defined in index.css.
 *
 * Used by the task drawer (Phase B) and anywhere else we surface task notes,
 * recaps, or other markdown bodies. Falls back to a muted "—" when notes are
 * empty so callers don't need to gate their layout.
 *
 * Security note: marked v14's default tokenizer does NOT strip raw HTML, but
 * v1 task notes come from Airtable (operator-authored, not client-submitted)
 * so we accept inline HTML for now. If we later expose user-submitted markdown,
 * swap this to `marked.parse(notes, { async: false, breaks: true })` paired
 * with DOMPurify before render.
 */
import { useMemo } from 'react';
import { marked } from 'marked';
import { cn } from '@/lib/utils';

interface MarkdownNotesProps {
  notes?: string | null;
  className?: string;
  /** Override the empty-state placeholder. Pass `null` to render nothing when empty. */
  emptyState?: string | null;
}

// Configure marked once at module load.
marked.setOptions({
  gfm: true,
  breaks: true,
});

export function MarkdownNotes({ notes, className, emptyState = '—' }: MarkdownNotesProps) {
  const html = useMemo(() => {
    if (!notes || notes.trim().length === 0) return null;
    try {
      // marked.parse is sync when async option is not set.
      return marked.parse(notes, { async: false }) as string;
    } catch {
      return null;
    }
  }, [notes]);

  if (!html) {
    if (emptyState === null) return null;
    return <span className={cn('text-muted-foreground', className)}>{emptyState}</span>;
  }

  return (
    <div
      className={cn('prose-notes', className)}
      // Notes are operator-authored markdown from Airtable; see security note above.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
